import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles, Mail, MessageCircle, Link as LinkIcon, Clock, FileText, Image as ImageIcon, Download } from "lucide-react";
import { format } from "date-fns";

function fileUrl(url) {
  // Convert relative /api/files/xxx/download → absolute with auth query param for direct access.
  if (!url) return "#";
  if (url.startsWith("http")) return url;
  const token = localStorage.getItem("eod_token") || "";
  const sep = url.includes("?") ? "&" : "?";
  return `${process.env.REACT_APP_BACKEND_URL}${url}${sep}auth=${encodeURIComponent(token)}`;
}

function AttachmentRow({ a }) {
  const isFile = a.kind === "file";
  const isImage = isFile && (a.content_type || "").startsWith("image/");
  const href = isFile ? fileUrl(a.url) : a.url;
  const Icon = isImage ? ImageIcon : isFile ? FileText : LinkIcon;
  return (
    <a href={href} target="_blank" rel="noreferrer"
      data-testid={`drawer-attachment-${a.file_id || "link"}`}
      className="flex items-center gap-3 p-3 rounded-lg bg-[#18181b] border border-[#27272a] hover:border-indigo-500/40 text-sm text-zinc-200 group">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center border ${isFile ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-indigo-500/10 border-indigo-500/30 text-indigo-300"}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="truncate">{a.name || a.url}</div>
        {isFile && (
          <div className="text-xs text-zinc-500 font-mono">
            {a.content_type || "file"} · {a.size ? `${Math.max(1, Math.round(a.size / 1024))} KB` : "—"}
          </div>
        )}
      </div>
      <Download className="w-4 h-4 text-zinc-500 group-hover:text-zinc-200" />
    </a>
  );
}

export default function ReportDrawer({ open, onOpenChange, report }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" data-testid="report-side-drawer"
        className="w-full sm:max-w-lg bg-[#0f0f12] border-[#27272a] text-zinc-100 overflow-y-auto">
        {report ? (
          <>
            <SheetHeader className="text-left">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="border-indigo-500/40 text-indigo-300 bg-indigo-500/10 capitalize">
                  {report.source === "email" ? <Mail className="w-3 h-3 mr-1" /> : <MessageCircle className="w-3 h-3 mr-1" />}
                  {report.source}
                </Badge>
                <span className="text-xs text-zinc-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {format(new Date(report.received_at), "PPp")}
                </span>
              </div>
              <SheetTitle className="font-display text-xl">{report.subject || "EOD Report"}</SheetTitle>
              <SheetDescription className="text-zinc-400">
                From <span className="text-zinc-200">{report.manager_name}</span> · Business date {report.submission_date}
              </SheetDescription>
            </SheetHeader>
            <Tabs defaultValue="summary" className="mt-6">
              <TabsList className="bg-[#18181b] border border-[#27272a]">
                <TabsTrigger value="summary" data-testid="drawer-tab-summary">
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" /> AI Summary
                </TabsTrigger>
                <TabsTrigger value="body" data-testid="drawer-tab-body">Notes</TabsTrigger>
                <TabsTrigger value="links" data-testid="drawer-tab-links">Attachments</TabsTrigger>
              </TabsList>
              <TabsContent value="summary" className="mt-4">
                <div data-testid="report-drawer-ai-summary"
                  className="p-4 rounded-lg bg-gradient-to-br from-indigo-500/5 to-transparent border border-indigo-500/20 text-sm leading-relaxed text-zinc-200">
                  {report.ai_summary || "No summary available."}
                </div>
              </TabsContent>
              <TabsContent value="body" className="mt-4">
                <pre className="whitespace-pre-wrap text-sm text-zinc-300 bg-[#18181b] border border-[#27272a] rounded-lg p-4 font-sans leading-relaxed">
{report.body || "(no notes)"}
                </pre>
              </TabsContent>
              <TabsContent value="links" className="mt-4 space-y-2">
                {(report.attachments || []).length === 0 && (
                  <div className="text-sm text-zinc-500">No attachments or links.</div>
                )}
                {(report.attachments || []).map((a, i) => (
                  <AttachmentRow key={a.file_id || i} a={a} />
                ))}
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <div className="text-zinc-400">No report selected.</div>
        )}
      </SheetContent>
    </Sheet>
  );
}
